import { motion } from "framer-motion";
import { ArrowDownRight, Plus } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

// Clean medical equipment — Da Vinci-style surgical robot close-up (Unsplash, Marcel Scholte)
const HERO_BG = "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=2400&q=80";

export default function Hero() {
  const { t } = useLang();
  const H = t.hero;
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden border-b border-white/10"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${HERO_BG}')`,
          filter: "grayscale(0.2) contrast(1.05) brightness(0.62)",
        }}
      />
      {/* Stronger left-side darkening; bottom fade to black */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#101012] via-[#101012]/65 to-[#101012]/25" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#101012]/40 via-transparent to-[#101012]" />
      <div className="absolute inset-0 grid-lines opacity-60" />
      <div className="absolute inset-0 noise" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 pt-24 lg:pt-28 grid grid-cols-12 gap-6">
        <div className="col-span-12 flex items-center justify-between text-white/60">
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
            <span className="ticker-tick" />
            <span>{H.est}</span>
          </div>
          <div className="hidden md:flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.22em]">
            <span>N 35.8714°</span>
            <span>E 128.6014°</span>
            <span>UTC+09</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-9">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="label-mono mb-6"
          >
            {H.eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="display-1 text-white text-[12vw] md:text-[9vw] lg:text-[7.5vw] xl:text-[7.5rem]"
          >
            {H.title_a}
            <br />
            {H.title_b}
            <br />
            <span className="italic font-light text-white/90">{H.title_italic}</span>{" "}
            <span className="text-[#FF3333]">{H.title_accent}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 text-white/70 text-base md:text-lg max-w-xl leading-relaxed"
          >
            {H.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#FF3333] hover:bg-[#E60000] text-white px-6 py-4 font-mono text-xs tracking-[0.18em] uppercase transition-colors"
            >
              {H.cta_primary}
              <ArrowDownRight size={16} className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
            </a>
            <a
              href="#domains"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 border border-white/25 hover:border-white text-white px-6 py-4 font-mono text-xs tracking-[0.18em] uppercase transition-colors"
            >
              <Plus size={14} />
              {H.cta_secondary}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="hidden lg:block col-span-3 self-end"
        >
          <div className="border border-white/15 bg-[#101012]/60 backdrop-blur-md p-6">
            <div className="label-mono mb-4">{H.study_label}</div>
            <div className="font-display text-2xl text-white leading-tight">
              {H.study_name} <span className="text-[#FF3333]">v2.4</span>
            </div>
            <div className="mt-4 font-mono text-[10px] tracking-widest text-white/40 uppercase">
              {H.study_meta}
            </div>
            <div className="mt-6 h-px bg-white/10" />
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <div className="label-mono">{H.sus}</div>
                <div className="font-display text-3xl text-white mt-1">87.3</div>
              </div>
              <div>
                <div className="label-mono">{H.task}</div>
                <div className="font-display text-3xl text-white mt-1">94<span className="text-white/40 text-xl">%</span></div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-[#101012]/60 backdrop-blur-md">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4 grid grid-cols-12 gap-4 items-center">
          <div className="col-span-12 md:col-span-4 font-mono text-[10px] tracking-[0.22em] uppercase text-white/50">
            {H.scroll}
          </div>
          <div className="col-span-12 md:col-span-8 flex justify-end gap-6 lg:gap-8 font-mono text-[10px] tracking-[0.18em] uppercase text-white/50 flex-wrap">
            {H.meta_chips.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
